import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { NAV } from "./nav";
import client from "../lib/api";
import { DatasetSelector, Brand } from "./common";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { LogOut, ChevronDown, Rows3, Check } from "lucide-react";

const ROLE_LABEL = {
  super_admin: "Super Admin", admin: "Administrator", analyst: "Process Analyst",
  viewer: "Viewer", connector_admin: "Connector Admin",
};

const DENSITIES = [
  { key: "comfortable", label: "Comfortable", zoom: 1, hint: "Roomy spacing" },
  { key: "cozy", label: "Cozy", zoom: 0.92, hint: "Slightly tighter" },
  { key: "compact", label: "Compact", zoom: 0.84, hint: "Denser content" },
  { key: "ultra", label: "Ultra", zoom: 0.74, hint: "Maximum content, least scrolling" },
];

function TenantSwitcher() {
  const { isSuper, activeTenantId, selectTenant } = useAuth();
  const [tenants, setTenants] = useState([]);
  useEffect(() => {
    if (!isSuper) return;
    client.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
  }, [isSuper]);
  if (!isSuper) return null;
  return (
    <Select value={activeTenantId || ""} onValueChange={selectTenant}>
      <SelectTrigger className="glass border-white/60 rounded-xl h-10 w-auto shrink-0 min-w-[160px]" data-testid="tenant-switcher">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-cyan-700">Tenant</span>
          <SelectValue placeholder="Select tenant" />
        </div>
      </SelectTrigger>
      <SelectContent>
        {tenants.map((t) => (
          <SelectItem key={t.id} value={t.id} data-testid={`tenant-opt-${t.id}`}>{t.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function DensityChooser() {
  const { density, setDensity } = useApp();
  const current = DENSITIES.find((d) => d.key === density) || DENSITIES[2];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2 glass rounded-xl px-3 h-10 pm-transition hover:bg-white/80" data-testid="density-trigger" title="Content density">
          <Rows3 className="w-4 h-4 text-[#0e7aa6]" />
          <span className="text-sm font-medium text-slate-700 hidden lg:block">{current.label}</span>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Content density</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {DENSITIES.map((d) => (
          <DropdownMenuItem key={d.key} onClick={() => setDensity(d.key)} data-testid={`density-${d.key}`} className="cursor-pointer">
            <div className="flex-1">
              <div className="text-sm font-medium">{d.label}</div>
              <div className="text-[11px] text-slate-400">{d.hint}</div>
            </div>
            {density === d.key && <Check className="w-4 h-4 text-[#0e7aa6]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function UserMenu() {
  const { user, tenant, logout } = useAuth();
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-2.5 glass rounded-xl pl-1.5 pr-3 py-1.5 pm-transition hover:bg-white/80" data-testid="user-menu-trigger">
          <div className="w-8 h-8 rounded-lg pm-gradient-dark flex items-center justify-center text-white text-xs font-bold overflow-hidden">
            {user?.avatar ? <img src={user.avatar} alt="" className="w-full h-full object-cover" /> : initials}
          </div>
          <div className="text-left leading-none hidden sm:block whitespace-nowrap">
            <div className="text-sm font-semibold text-slate-800 truncate max-w-[140px]">{user?.name}</div>
            <div className="text-[11px] text-slate-500">{ROLE_LABEL[user?.role]}</div>
          </div>
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="text-sm font-semibold">{user?.name}</div>
          <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
          {tenant && <div className="text-xs text-cyan-600 mt-1">{tenant.name}</div>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-600">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function visibleSections(can) {
  return NAV.map((s) => ({ ...s, items: s.items.filter((i) => can(i.min)) })).filter((s) => s.items.length);
}

export default function Shell() {
  const { can } = useAuth();
  const { density } = useApp();
  const sections = visibleSections(can);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const zoom = (DENSITIES.find((d) => d.key === density) || DENSITIES[2]).zoom;

  return (
    <div className="min-h-screen pm-app-bg flex flex-col">
      {/* Sticky top header with ALL navigation */}
      <header className="sticky top-0 z-40 glass-strong border-b border-white/50" data-testid="app-header">
        {/* Top row: brand + context controls */}
        <div className="px-3 md:px-6 min-h-16 py-2 flex items-center flex-wrap gap-2 md:gap-3">
          <button onClick={() => navigate("/app")} data-testid="header-brand" className="shrink-0 pm-transition hover:opacity-80">
            <Brand size="md" />
          </button>
          <div className="flex-1 min-w-0" />
          <TenantSwitcher />
          <div className="hidden sm:block"><DatasetSelector /></div>
          <DensityChooser />
          <UserMenu />
        </div>
        {/* Mobile dataset selector */}
        <div className="sm:hidden px-3 pb-2"><DatasetSelector className="w-full" /></div>
        {/* Nav row: crisp single line — contextual icons, active tab expands with label */}
        <nav className="px-2 md:px-4 pb-2.5" data-testid="header-nav">
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar bg-white/40 rounded-2xl p-1.5 ring-1 ring-white/60">
            {sections.map((sec, si) => (
              <React.Fragment key={sec.section}>
                {si > 0 && <span className="mx-1 h-6 w-px bg-slate-300/50 shrink-0" aria-hidden />}
                {sec.items.map((item) => {
                  const isActive = pathname === item.path;
                  const Icon = item.icon;
                  return (
                    <button key={item.path} onClick={() => navigate(item.path)} data-testid={`nav-${item.path}`} title={`${sec.section} · ${item.label}`}
                      className={`group shrink-0 flex items-center h-10 rounded-xl pm-transition ${isActive
                        ? "pm-gradient text-white shadow-lg shadow-sky-900/25 px-4 gap-2"
                        : "w-10 justify-center text-slate-500 hover:bg-white hover:text-[#0f2547] hover:shadow-sm"}`}>
                      <Icon className="w-[18px] h-[18px] shrink-0" />
                      {isActive && <span className="whitespace-nowrap text-sm font-semibold">{item.label}</span>}
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </nav>
      </header>

      {/* Content */}
      <main className="flex-1 min-w-0" data-testid="app-main">
        <div style={{ zoom }} className="p-4 md:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
